import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { buildNumberTextFieldInputProps } from '.';
import { FieldType } from '..';
import { InputValidationResult } from '../..';
import { StackNumberingEnumDto } from '../../../.generated/admin-api';
import { ApplicationState } from '../../../store';
import { apiTranslate } from '../../../translations/translations';
import { ValidatedTextInput } from '../../ValidatedTextInput';
import { stackActions } from '../../../store/stack/actions';

type StackNumberFieldInputProps = {
  field: FieldType;
  required: boolean;
  defaultValue?: number;
  disabled?: boolean;
  onChangeValidator: (value?: number) => InputValidationResult;
};

const StackNumberField: React.FC<StackNumberFieldInputProps> = (props) => {
  const { t } = useTranslation(['translations']);

  const { field, required, defaultValue, disabled, onChangeValidator } = props;

  const dispatch = useDispatch();

  const [stackNumber, setStackNumber] = useState<string>('');
  const [autoStackNumberInput, setAutoStackNumberInput] = useState<boolean>(
    false,
  );
  const [autoStackNumber, setAutoStackNumber] = useState<number>();

  const department = useSelector(
    (state: ApplicationState) => state.user.selectedDepartment,
  );

  const reservedStackNumbers = useSelector((state: ApplicationState) => {
    const department = state.user.selectedDepartment;

    if (!department) {
      return undefined;
    }

    return state.stacks.departments[department.id]?.reservedStackNumbers;
  });

  const changeValue = (value: string) => {
    setStackNumber(value);
    const newValue = value ? Number(value) : undefined;

    setAutoStackNumberInput(newValue === autoStackNumber);

    return onChangeValidator(newValue);
  };

  const handleChange = (value: string): InputValidationResult => {
    return changeValue(value);
  };

  const handleBlur = (value: string): InputValidationResult => {
    if (autoStackNumber && (value === undefined || value === '')) {
      return changeValue(autoStackNumber.toString());
    }

    return changeValue(value);
  };

  const handleFocus = () => {
    if (autoStackNumberInput) {
      changeValue('');
    }
  };

  useEffect(() => {
    if (autoStackNumber) {
      changeValue(autoStackNumber.toString());
    }
  }, [autoStackNumber]);

  useEffect(() => {
    if (defaultValue) {
      changeValue(defaultValue.toString());
    }
  }, []);

  useEffect(() => {
    if (defaultValue || stackNumber || disabled === true) {
      // If we already has a stack number we skip the auto stack number
      return;
    }

    if (
      department &&
      department.stackNumbering &&
      department.stackNumbering === StackNumberingEnumDto.AUTO_GENERATED &&
      reservedStackNumbers &&
      reservedStackNumbers.length > 0
    ) {
      const pickedStackNumber = reservedStackNumbers[0];
      setAutoStackNumber(pickedStackNumber);
    } else {
      setAutoStackNumber(undefined);
    }
  }, [department]);

  const helperText = autoStackNumberInput
    ? ''
    : t('registration_page.manual_input');

  return (
    <ValidatedTextInput
      type="number"
      inputName={field.id}
      required={required}
      label={apiTranslate(field.title)}
      placeholder={autoStackNumber?.toString()}
      value={stackNumber}
      fullWidth={false}
      helperText={helperText}
      onFocus={handleFocus}
      onChangeValidator={handleChange}
      onBlurValidator={handleBlur}
      InputProps={buildNumberTextFieldInputProps(field)}
      disabled={disabled}
    />
  );
};

export { StackNumberField };
